import React, { useState, useEffect, Fragment } from "react";
import useForm, { FormContext } from "react-hook-form";
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Grid
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CancelIcon from '@material-ui/icons/Cancel';
import TextField from "../form/TextField";
import Alert from "../../utils/alert";

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
  },
  field: {
    marginBottom: '1rem',
  },
  flexResponsive: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  fileUploadSection: {
    marginBottom: '1rem',
    flex: 1,
  },
  fileUploadLabel: {
    display: 'block',
    fontWeight: 'bold',
    marginBottom: '1rem',
    color: '#26344a',
    fontSize: '1.6rem',
  },
  fileUploadArea: {
    border: '2px dashed #dbdbdb',
    borderRadius: '4px',
    padding: '2rem',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#f9f9f9',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '120px',
    '&:hover': {
      borderColor: '#3273dc',
      backgroundColor: '#f0f8ff',
    },
  },
  fileInput: {
    display: 'none',
  },
  uploadIcon: {
    marginBottom: theme.spacing(1),
    fontSize: '2.5rem',
    color: '#3273dc',
  },
  fileLabel: {
    fontSize: '1.4rem',
    color: '#3273dc',
    fontWeight: 600,
    marginTop: theme.spacing(1),
  },
  selectedFile: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: '#f0f8ff',
    borderRadius: '4px',
    border: '1px solid #3273dc',
    minHeight: '50px',
  },
  fileName: {
    fontSize: '1.4rem',
    color: '#26344a',
    marginRight: '0.5rem',
    flex: 1,
  },
  cancelIcon: {
    color: '#ff3860',
    cursor: 'pointer',
    fontSize: '1.5rem',
    '&:hover': {
      opacity: 0.7,
    },
  },
  noFile: {
    fontSize: '1.4rem',
    color: '#7a7a7a',
    fontStyle: 'italic',
    marginTop: '1rem',
  },
  submitButton: {
    backgroundColor: '#3273dc',
    color: '#fff',
    fontSize: '1.6rem',
    padding: '0.75rem 2rem',
    textTransform: 'none',
    fontWeight: 600,
    marginTop: '2rem',
    '&:hover': {
      backgroundColor: '#2759a5',
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '2rem',
  },
}));

const MAX_FILE_SIZE = 1048576; //1 MB

export default function ApplyForm({ onSubmit, email, title }) {
  const classes = useStyles();
  const methods = useForm(); // initialise the hook
  const { handleSubmit, setValue } = methods;

  const [selectedCV, setCV] = useState(null);
  const [selectedCL, setCL] = useState(null);

  useEffect(() => {
    setValue("email", email);
    setValue("title", title);
  }, [email, title, setValue]);

  const handleFileChange = event => {
    let file = event.target.files[0];
    if (file.size > MAX_FILE_SIZE) {
      Alert.error(
        "ファイルサイズが大きすぎます！<br/><br/>１MB以下のファイルを選択してください。"
      );
      return;
    }
    if (event.target.name === "cv") {
      setCV(file);
    } else if (event.target.name === "cl") {
      setCL(file);
    }
  };

  const submitFormData = data => {
    let fd = { ...data };
    let attachments = [];
    if (selectedCV) attachments.push(selectedCV);
    if (selectedCL) attachments.push(selectedCL);
    fd.attachments = attachments;
    onSubmit(fd);
  };

  return (
    <Box>
      <FormContext {...methods}>
        <form onSubmit={handleSubmit(submitFormData)} className={classes.form}>
          <div className={classes.field}>
            <TextField
              label="件名"
              type="text"
              placeholder="件名を入力してください"
              name="title"
              registerOptions={{ required: true, maxLength: 100 }}
            />
          </div>

          <div className={classes.flexResponsive}>
            <div className={classes.field}>
              <TextField
                label="氏名"
                type="text"
                placeholder="氏名を入力してください"
                name="name"
                registerOptions={{ required: true, maxLength: 100 }}
              />
            </div>
            <div className={classes.field}>
              <TextField
                label="返信用メールアドレス"
                type="email"
                placeholder="メールアドレスを入力してください"
                name="email"
                info="広告主が返信する際に利用する為、アドレスに間違いが無いか必ずご確認ください"
                registerOptions={{ required: true, maxLength: 100 }}
              />
            </div>
          </div>

          <div className={classes.field}>
            <TextField
              label="メッセージ"
              type="textarea"
              name="message"
              rows={15}
              cols={20}
              registerOptions={{ required: true, maxLength: 5000 }}
            />
          </div>

          <div className={classes.flexResponsive}>
            <div className={classes.fileUploadSection}>
              <Typography variant="body1" className={classes.fileUploadLabel}>
                CV アップロード
              </Typography>
              <label className={classes.fileUploadArea}>
                <input
                  className={classes.fileInput}
                  type="file"
                  name="cv"
                  onChange={handleFileChange}
                  accept=".doc,.docx,.txt,.pdf"
                />
                <CloudUploadIcon className={classes.uploadIcon} />
                <Typography variant="body1" className={classes.fileLabel}>
                  ファイルを選択してください
                </Typography>
              </label>
              <div>
                {selectedCV ? (
                  <Box className={classes.selectedFile}>
                    <Typography variant="body2" className={classes.fileName}>
                      {selectedCV.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setCV(null)}
                      className={classes.cancelIcon}
                    >
                      <CancelIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography variant="body2" className={classes.noFile}>
                    No file
                  </Typography>
                )}
              </div>
            </div>

            <div className={classes.fileUploadSection}>
              <Typography variant="body1" className={classes.fileUploadLabel}>
                カバーレター
              </Typography>
              <label className={classes.fileUploadArea}>
                <input
                  className={classes.fileInput}
                  type="file"
                  name="cl"
                  onChange={handleFileChange}
                  accept=".doc,.docx,.txt,.pdf"
                />
                <CloudUploadIcon className={classes.uploadIcon} />
                <Typography variant="body1" className={classes.fileLabel}>
                  ファイルを選択してください
                </Typography>
              </label>
              <div>
                {selectedCL ? (
                  <Box className={classes.selectedFile}>
                    <Typography variant="body2" className={classes.fileName}>
                      {selectedCL.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setCL(null)}
                      className={classes.cancelIcon}
                    >
                      <CancelIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography variant="body2" className={classes.noFile}>
                    No file
                  </Typography>
                )}
              </div>
            </div>
          </div>

          <Box className={classes.buttonContainer}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              className={classes.submitButton}
            >
              送信
            </Button>
          </Box>
        </form>
      </FormContext>
    </Box>
  );
}
